import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Zap, Shield, Check, Wrench } from 'lucide-react';
import AutoMode from '@mui/icons-material/AutoMode';
import { BreadcrumbSchema, FAQSchema } from '@/components/JsonLd';
import SEOEVCalculator from '@/components/seo/SEOEVCalculator';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

const serif = { fontFamily: '"Playfair Display", Georgia, serif' };

export default function ChargerPageTemplate({ page }) {
  if (!page) return null;

  const { pricing_data: p, content_sections: sections, faqs, h1, intro_text } = page;
  const charger = p?.charger;
  const evData = p?.evData;

  const lastUpdated = page.last_data_refresh || page.updated_date || page.created_date;
  const formattedDate = lastUpdated ? new Date(lastUpdated).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' }) : null;

  return (
    <article itemScope itemType="https://schema.org/Product" style={{ fontFamily: '"DM Sans", "Space Grotesk", sans-serif' }}>
      <BreadcrumbSchema items={[
        { name: 'Forside', path: '/' },
        { name: 'Opladere', path: '/opladere' },
        { name: charger?.name || h1, path: `/sammenlign/${page.slug}` },
      ]} />
      {faqs?.length > 0 && <FAQSchema faqs={faqs} />}

      {/* Hero */}
      <section className="bg-[#1a3a32] text-white py-12 md:py-20">
        <div className="container mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/90 rounded-full px-4 py-1.5 text-sm font-semibold mb-6">
            <Wrench className="w-4 h-4" /> {charger?.brand} · {charger?.kw} kW
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-6" style={serif} itemProp="name">{h1}</h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-8" itemProp="description">{intro_text}</p>
          {formattedDate && (
            <p className="text-sm text-white/60 mb-4">
              Opdateret <time dateTime={new Date(lastUpdated).toISOString()}>{formattedDate}</time>
            </p>
          )}

          {charger && (
            <div className="inline-flex flex-col items-center gap-2">
              <div className="flex items-baseline gap-3">
                {charger.originalPrice > charger.price && (
                  <span className="text-lg text-white/50 line-through">{charger.originalPrice.toLocaleString('da-DK')} kr.</span>
                )}
                <span className="text-4xl md:text-5xl font-bold text-[#eab308]" style={serif}>{charger.price.toLocaleString('da-DK')} kr.</span>
              </div>
              <p className="text-sm text-white/70">inkl. el-aftale · Du betaler med din første el-regning</p>
            </div>
          )}
        </div>
      </section>

      {/* Included features grid */}
      <section className="py-10 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto grid sm:grid-cols-3 gap-4">
            {[
              { icon: <AutoMode sx={{ fontSize: 24 }} />, label: 'Saving Mode', value: '0 kr./md.', desc: `Spar ~${evData?.smartChargeYearly?.toLocaleString('da-DK') || '700'} kr./år` },
              { icon: <Shield className="w-6 h-6" />, label: 'Serviceaftale', value: '0 kr./md.', desc: `Værdi: ${evData?.serviceAgreementYearly?.toLocaleString('da-DK') || '588'} kr./år` },
              { icon: <Zap className="w-6 h-6" />, label: 'Laderabat', value: '10 øre/kWh', desc: `~${evData?.laderabatYearly?.toLocaleString('da-DK') || '396'} kr./år` },
            ].map((item, i) => (
              <div key={i} className="bg-[#f5f3ee] rounded-2xl border border-[#e8e4d9] p-5 text-center">
                <div className="w-12 h-12 bg-[#1a2e1a] rounded-xl flex items-center justify-center mx-auto mb-3 text-[#eab308]">
                  {item.icon}
                </div>
                <p className="font-bold text-gray-900">{item.label}</p>
                <p className="text-[#1a2e1a] font-bold text-lg">{item.value}</p>
                <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Charger specs */}
      {charger?.features?.length > 0 && (
        <section className="py-10 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold mb-6 text-center text-gray-900" style={serif}>Specifikationer</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {charger.features.map((f, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-3 text-center">
                    <Check className="w-5 h-5 text-[#1a2e1a] mx-auto mb-1" />
                    <span className="text-sm font-medium text-gray-700">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Content Sections (skip types already rendered by template) */}
      {sections?.filter(s => !['specs','included','social_proof','faq_intro'].includes(s.type)).map((section, i) => (
        <section key={i} className={`py-12 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold mb-4 text-gray-900" style={serif}>{section.heading}</h2>
              <div className="text-gray-700 leading-relaxed whitespace-pre-line">{section.body}</div>
            </div>
          </div>
        </section>
      ))}

      {/* EV Calculator */}
      <SEOEVCalculator competitorName={null} elSavings={0} />

      {/* FAQ */}
      {faqs?.length > 0 && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-center mb-8 text-gray-900" style={serif}>Ofte stillede spørgsmål</h2>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <details key={i} className="bg-gray-50 rounded-xl border border-gray-200 group">
                    <summary className="px-6 py-4 cursor-pointer font-semibold flex items-center justify-between text-gray-900">
                      {faq.question}
                      <span className="group-open:rotate-45 transition-transform text-2xl">+</span>
                    </summary>
                    <div className="px-6 pb-4 text-gray-600">{faq.answer}</div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-[#1a3a32]">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" style={serif}>
            Bestil {charger?.name || 'din ladeboks'} i dag
          </h2>
          <p className="text-gray-300 mb-6 max-w-lg mx-auto">
            {charger?.price?.toLocaleString('da-DK')} kr. inkl. el-aftale · Du betaler ikke noget i dag
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/opladere">
              <Button size="lg" className="bg-[#eab308] hover:bg-[#ca9a06] text-gray-900 font-bold">
                Se alle ladebokse <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/elaftale">
              <Button size="lg" variant="outline" className="border-2 border-white text-white font-semibold hover:bg-white hover:text-[#1a2e1a]">
                El-aftale uden ladeboks
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <SEODisclaimer page={page} />
    </article>
  );
}